import { Link } from 'react-router-dom';

/**
 * Shared links for money tables (Refunds, Invoices, Transactions, Parent
 * billing). Every customer, player and record reference should take an admin
 * straight to the page for that thing instead of showing a dead UUID.
 *
 * Each renders plain text when there is no id to link to, so a missing
 * relation degrades gracefully instead of producing a broken link.
 */

const base =
  'text-emerald-700 hover:text-emerald-800 hover:underline underline-offset-2 font-medium transition-colors';

export const ParentLink = ({ id, name, email, className = '', testId }) => {
  if (!name && !id) return <span className="text-slate-400">—</span>;
  const label = name || 'View parent';
  return (
    <span className={className}>
      {id ? (
        <Link to={`/admin/parents/${id}`} className={base} data-testid={testId || 'parent-link'}>
          {label}
        </Link>
      ) : (
        <span className="font-medium">{label}</span>
      )}
      {email && <span className="block text-xs text-slate-500">{email}</span>}
    </span>
  );
};

export const PlayerLink = ({ id, name, className = '', testId }) => {
  if (!name) return null;
  return id ? (
    <Link
      to={`/admin/players/${id}`}
      className={`${base} ${className}`}
      data-testid={testId || 'player-link'}
    >
      {name}
    </Link>
  ) : (
    <span className={className}>{name}</span>
  );
};

/** Multiple players in one cell (invoices can cover siblings). */
export const PlayerLinks = ({ players = [], names, className = '' }) => {
  const list = players.length
    ? players
    : String(names || '')
        .split(',')
        .map((n) => ({ name: n.trim() }))
        .filter((p) => p.name);
  if (!list.length) return <span className="text-slate-400">—</span>;
  return (
    <span className={className}>
      {list.map((p, i) => (
        <span key={p.id || p.name || i}>
          {i > 0 && ', '}
          <PlayerLink id={p.id} name={p.name} />
        </span>
      ))}
    </span>
  );
};

/**
 * A reference to the thing being paid for — enrollment, tournament, camp,
 * lane booking, invoice. `route` comes from the API (`entity_route`) when
 * available; otherwise we fall back to a sensible admin list page.
 */
const FALLBACK_ROUTES = {
  enrollment: '/admin/enrollments',
  tournament_registration: '/admin/tournaments',
  camp_registration: '/admin/camps',
  lane_booking: '/admin/lanes',
  manual_invoice: '/admin/invoice-management',
  small_group_invoice: '/admin/invoice-management',
};

export const EntityRefLink = ({ route, entityType, label, id, className = '', testId }) => {
  const to = route || FALLBACK_ROUTES[entityType] || null;
  const text = label || id || '—';
  if (!to) return <span className={className}>{text}</span>;
  return (
    <Link to={to} className={`${base} ${className}`} data-testid={testId || 'entity-link'}>
      {text}
    </Link>
  );
};

/** Enrollment id / invoice id shown as a link rather than raw text. */
export const EnrollmentLink = ({ id, label, className = '', testId }) => {
  if (!id) return <span className="text-slate-400">—</span>;
  return (
    <Link
      to={`/admin/enrollments/${id}`}
      className={`${base} font-mono text-xs ${className}`}
      data-testid={testId || 'enrollment-link'}
    >
      {label || id}
    </Link>
  );
};
